import { useEffect } from "react"
import { NextSeo } from "next-seo"

import styled from "styled-components"
import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import { getSite } from "@lib/site"

import { Container } from "@components/ui"
import { RichText } from "@components/common"

import { useTracking, useGeoIp } from "@hooks/index"

export default function Custom404({
  site
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { trackPage } = useTracking()
  const geoIp = useGeoIp()
  useEffect(() => {
    if (geoIp?.country_code) {
      trackPage({
        category: "404",
      })
    }
  }, [geoIp])

  return (
    <>
      <NextSeo title="404 Not Found" />
      <Container>
        <PageNotFound>
          <RichText richText={site.pageNotFound} />
        </PageNotFound>
      </Container>
    </>
  )
}

const PageNotFound = styled.div`
  max-width: 110rem;
  padding: 0 2rem;
  margin: 5rem auto 0;
  text-align: center;

  h1:not(.placeholder__title) {
    margin: 0 0 5rem;
    font-size: 3rem;
    line-height: 3.6rem;
    letter-spacing: 0.12em;
    font-weight: 400;
  }

  p {
    font-family: ${({ theme }) => theme.font.heading};
    max-width: 60rem;
    margin: 0 auto 4.4rem;
    font-size: 2rem;
    line-height: 3.4rem;
  }

  .placeholder-thumbnail:nth-of-type(2n) {
    margin-right: 0;
  }

  .rich-text__button-wrapper {
    margin: 0 0 6rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding: 0;

    h1:not(.placeholder__title) {
      font-size: 4rem;
      line-height: 4.8rem;
    }

    .fluid-image {
      height: 39rem;
    }

    .placeholder-thumbnail {
      &:nth-of-type(2n) {
        margin-right: 2rem;
      }

      &:nth-of-type(4n) {
        margin-right: 0;
      }
    }
  }
`

export async function getStaticProps({
  preview = false,
  locale = "en-US",
}: GetStaticPropsContext) {
  const site = await getSite({ preview, locale })

  return {
    props: {
      site
    },
  }
}
